<template>
  <div style="position:relative;" @click.stop>
    <!-- Readonly text input (placeholder=" " keeps floating-label compat) -->
    <input
      ref="trigger"
      :value="displayValue"
      :name="name"
      :disabled="disabled"
      type="text"
      readonly
      placeholder=" "
      class="form-control"
      :style="disabled ? 'padding-right:2.5rem;' : 'cursor:pointer;padding-right:2.5rem;'"
      @click="openPicker"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
    />
    <!-- Calendar icon -->
    <svg
      style="position:absolute;right:12px;top:50%;transform:translateY(-50%);width:18px;height:18px;color:#9197A1;pointer-events:none;"
      fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>

    <!-- Calendar popup — Portal appends to <body>, escaping ancestor CSS transforms -->
    <portal v-if="showPicker">
      <div class="fm-cal" :style="pickerStyle" @click.stop>

        <!-- Nav header — label doubles as a jump-to-month-grid / jump-to-year-grid
             trigger (click "March 2026" to see all months of 2026, then click
             "2026" to see a decade grid of years). This is what lets you jump
             straight to a far past/future year instead of clicking the arrow
             12+ times per year. -->
        <div class="fm-cal__nav">
          <button type="button" class="fm-cal__nav-btn" @click="navPrev">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
            type="button"
            class="fm-cal__month"
            :class="{ 'fm-cal__month--static': viewMode === 'years' }"
            :disabled="viewMode === 'years'"
            @click="onLabelClick"
          >{{ navLabel }}</button>
          <button type="button" class="fm-cal__nav-btn" @click="navNext">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Day view -->
        <template v-if="viewMode === 'days'">
          <!-- Day-of-week headers -->
          <div class="fm-cal__weekdays">
            <span v-for="h in ['Su','Mo','Tu','We','Th','Fr','Sa']" :key="h">{{ h }}</span>
          </div>

          <!-- Day cells -->
          <div class="fm-cal__days">
            <button
              v-for="day in calendarDays"
              :key="day.key"
              type="button"
              :disabled="!day.selectable"
              @click="selectDate(day)"
              :class="[
                'fm-cal__day',
                day.date === value                ? 'fm-cal__day--selected' : '',
                day.isToday && day.date !== value  ? 'fm-cal__day--today'    : '',
                !day.inMonth                       ? 'fm-cal__day--outside'  : '',
                !day.selectable && day.inMonth     ? 'fm-cal__day--disabled' : '',
              ]"
            >{{ day.d }}</button>
          </div>
        </template>

        <!-- Month grid — jump to any month of the year currently in view -->
        <div v-else-if="viewMode === 'months'" class="fm-cal__grid fm-cal__grid--months">
          <button
            v-for="m in monthGridMonths"
            :key="m.index"
            type="button"
            :disabled="!m.selectable"
            @click="selectMonth(m.index)"
            :class="[
              'fm-cal__grid-cell',
              m.isCurrent               ? 'fm-cal__grid-cell--selected' : '',
              !m.selectable             ? 'fm-cal__grid-cell--disabled' : '',
            ]"
          >{{ m.label }}</button>
        </div>

        <!-- Year grid (decade view) — the actual "jump to a past/future year"
             control. First/last cells are the adjoining decade, dimmed, same
             convention as the outside-month day cells above. -->
        <div v-else class="fm-cal__grid fm-cal__grid--years">
          <button
            v-for="y in yearGridYears"
            :key="y.year"
            type="button"
            :disabled="!y.selectable"
            @click="selectYear(y.year)"
            :class="[
              'fm-cal__grid-cell',
              y.isCurrent                ? 'fm-cal__grid-cell--selected' : '',
              !y.inDecade                ? 'fm-cal__grid-cell--outside'  : '',
              !y.selectable              ? 'fm-cal__grid-cell--disabled' : '',
            ]"
          >{{ y.year }}</button>
        </div>

      </div>
    </portal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import Portal from '@modules/shared/components/Portal.vue';

export default {
  name: 'CalendarInput',
  components: { Portal },
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: String, default: '' },
    name:  { type: String, default: null },
    // Max selectable date (YYYY-MM-DD). Defaults to today — suitable for DOB fields.
    maxDate: { type: String, default: null },
    // Month to show initially when no value is set (YYYY-MM-DD).
    // Default: 30 years ago — good for DOB. Pass null for current month.
    initialView: { type: String, default: null },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      showPicker:  false,
      pickerStyle: {},
      viewDate: this._resolveViewDate(this.value, this.initialView),
      // 'days' (default calendar grid) | 'months' (12-month grid for a year)
      // | 'years' (decade grid) — lets you jump straight to a far year
      // instead of clicking the month arrow dozens of times.
      viewMode: 'days',
      // First year shown in the decade grid, e.g. 2020 for a 2020-2029 view.
      // Only meaningful while viewMode === 'years'.
      yearGridStart: null,
    };
  },
  computed: {
    todayStr()     { return dayjs().format('YYYY-MM-DD'); },
    effectiveMax() { return this.maxDate || this.todayStr; },
    displayValue() {
      return this.value ? dayjs(this.value).format('D MMM YYYY') : '';
    },
    monthLabel() {
      return this.viewDate.format('MMMM YYYY');
    },
    // Text shown in the nav header — depends on which grid is currently open.
    navLabel() {
      if (this.viewMode === 'years')  return `${this.yearGridStart} - ${this.yearGridStart + 9}`;
      if (this.viewMode === 'months') return this.viewDate.format('YYYY');
      return this.monthLabel;
    },
    // 12 cells for the month grid (viewMode === 'months'), all within the
    // year currently held in viewDate.
    monthGridMonths() {
      const year    = this.viewDate.year();
      const maxDate = this.effectiveMax;
      const months  = [];
      for (let m = 0; m < 12; m++) {
        const monthStart = dayjs().year(year).month(m).startOf('month');
        months.push({
          index:      m,
          label:      monthStart.format('MMM'),
          isCurrent:  m === this.viewDate.month(),
          selectable: monthStart.format('YYYY-MM-DD') <= maxDate,
        });
      }
      return months;
    },
    // 12 cells for the decade grid (viewMode === 'years'): the decade itself
    // plus one dimmed "outside" cell on each end, matching the outside-month
    // convention already used for the day grid.
    yearGridYears() {
      const start   = this.yearGridStart;
      const maxDate = this.effectiveMax;
      const maxYear = dayjs(maxDate).year();
      const years   = [];
      for (let i = -1; i <= 10; i++) {
        const year = start + i;
        years.push({
          year,
          inDecade:   i >= 0 && i <= 9,
          isCurrent:  year === this.viewDate.year(),
          selectable: year <= maxYear,
        });
      }
      return years;
    },
    calendarDays() {
      const start       = this.viewDate.startOf('month');
      const offset      = start.day(); // 0 = Sunday
      const daysInMonth = start.daysInMonth();
      const maxDate     = this.effectiveMax;
      const todayStr    = this.todayStr;
      const days        = [];

      // Pad with previous-month days
      const prevMonth = start.subtract(1, 'month');
      for (let i = offset - 1; i >= 0; i--) {
        const d    = prevMonth.daysInMonth() - i;
        const date = prevMonth.date(d).format('YYYY-MM-DD');
        days.push({ d, date, inMonth: false, selectable: false, isToday: false, key: `p${d}` });
      }
      // Current month
      for (let d = 1; d <= daysInMonth; d++) {
        const date       = start.date(d).format('YYYY-MM-DD');
        const selectable = date <= maxDate;
        days.push({ d, date, inMonth: true, selectable, isToday: date === todayStr, key: `c${d}` });
      }
      // Pad to 42 cells
      const nextStart = start.add(1, 'month');
      for (let d = 1; days.length < 42; d++) {
        const date = nextStart.date(d).format('YYYY-MM-DD');
        days.push({ d, date, inMonth: false, selectable: false, isToday: false, key: `n${d}` });
      }
      return days;
    },
  },
  mounted() {
    this._onOutsideClick = () => {
      this._removeListeners();
      this.showPicker = false;
    };
    document.addEventListener('click', this._onOutsideClick);
    // Escape closes the picker and returns focus to the trigger — without
    // this a keyboard user who opened the picker (via Enter/Space) has no
    // way to close it again without a mouse.
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.showPicker) {
        this._removeListeners();
        this.showPicker = false;
        this.$nextTick(() => this.$refs.trigger?.focus());
      }
    };
    document.addEventListener('keydown', this._onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this._onOutsideClick);
    document.removeEventListener('keydown', this._onKeydown);
    this._removeListeners();
  },
  methods: {
    _resolveViewDate(value, initialView) {
      if (value)       return dayjs(value).startOf('month');
      if (initialView) return dayjs(initialView).startOf('month');
      return dayjs().subtract(30, 'year').startOf('month');
    },
    _calcStyle() {
      if (!this.$refs.trigger) return;
      const rect = this.$refs.trigger.getBoundingClientRect();
      this.pickerStyle = {
        position: 'fixed',
        zIndex:   9999,
        top:      (rect.bottom + 6) + 'px',
        left:     rect.left + 'px',
        width:    Math.max(rect.width, 280) + 'px',
      };
    },
    _removeListeners() {
      if (this._reposFn) {
        window.removeEventListener('scroll', this._reposFn, true);
        window.removeEventListener('resize', this._reposFn);
        this._reposFn = null;
      }
    },
    openPicker() {
      if (this.disabled) return;
      if (this.value) {
        this.viewDate = dayjs(this.value).startOf('month');
      }
      this.viewMode = 'days';
      this._calcStyle();
      this._reposFn = () => { if (this.showPicker) this._calcStyle(); };
      window.addEventListener('scroll', this._reposFn, { passive: true, capture: true });
      window.addEventListener('resize', this._reposFn, { passive: true });
      this.showPicker = true;
    },
    prevMonth() { this.viewDate = this.viewDate.subtract(1, 'month'); },
    nextMonth() { this.viewDate = this.viewDate.add(1, 'month'); },
    // Nav arrows step by whatever unit the currently-open grid represents:
    // a month in day view, a year in month view, a decade in year view.
    navPrev() {
      if (this.viewMode === 'years')       this.yearGridStart -= 10;
      else if (this.viewMode === 'months') this.viewDate = this.viewDate.subtract(1, 'year');
      else                                 this.prevMonth();
    },
    navNext() {
      if (this.viewMode === 'years')       this.yearGridStart += 10;
      else if (this.viewMode === 'months') this.viewDate = this.viewDate.add(1, 'year');
      else                                 this.nextMonth();
    },
    // Clicking the nav label drills one level up: day grid -> month grid ->
    // year grid. This is the actual "jump to a year" entry point.
    onLabelClick() {
      if (this.viewMode === 'days') {
        this.viewMode = 'months';
      } else if (this.viewMode === 'months') {
        this.yearGridStart = Math.floor(this.viewDate.year() / 10) * 10;
        this.viewMode = 'years';
      }
    },
    selectMonth(m) {
      this.viewDate = this.viewDate.month(m);
      this.viewMode = 'days';
    },
    selectYear(y) {
      this.viewDate = this.viewDate.year(y);
      this.viewMode = 'months';
    },
    selectDate(day) {
      if (!day.selectable) return;
      this.$emit('input', day.date);
      this._removeListeners();
      this.showPicker = false;
      // The value above is applied via v-model (a prop update), which never
      // fires a native 'input'/'change' DOM event on the trigger element.
      // FormGroup's floating-label logic (fm-has-value) listens for exactly
      // those native events to know when to keep the label floated, so
      // without this it never notices the new value and the label drops
      // back down once the input loses focus. $nextTick waits for Vue to
      // patch :value="displayValue" onto the DOM first, so the dispatched
      // event reflects the selected date.
      this.$nextTick(() => {
        if (this.$refs.trigger) {
          this.$refs.trigger.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    },
  },
};
</script>

<style lang="scss">
// Flash Money design-system tokens
$fm-ink:      #15171C;
$fm-ink-soft: #5B606B;
$fm-line:     #E7E4DA;
$fm-line-soft:#F0EEE7;
$fm-amber:    #FFB000;

.fm-cal {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid $fm-line;
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(21,23,28,.13), 0 2px 8px rgba(21,23,28,.06);
  overflow: hidden;

  &__nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid $fm-line-soft;
  }

  &__nav-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: $fm-line-soft;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: $fm-ink-soft;
    padding: 0;
    transition: background 0.15s;
    &:hover { background: $fm-line; }
    svg { width: 15px; height: 15px; stroke-width: 2.2; }
  }

  &__month {
    font-weight: 600;
    font-size: 14px;
    color: $fm-ink;
    user-select: none;
    background: none;
    border: none;
    border-radius: 6px;
    padding: 2px 8px;
    cursor: pointer;
    transition: background 0.12s;
    &:hover:not(:disabled) { background: $fm-line-soft; }
    &--static { cursor: default; &:hover { background: none; } }
  }

  &__grid {
    display: grid;
    gap: 6px;
    padding: 12px;
  }
  &__grid--months { grid-template-columns: repeat(3, 1fr); }
  &__grid--years  { grid-template-columns: repeat(4, 1fr); }

  &__grid-cell {
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 13px;
    border: none;
    background: none;
    cursor: pointer;
    transition: background 0.12s;
    color: $fm-ink;

    &--selected {
      background: $fm-amber !important;
      color: $fm-ink;
      font-weight: 600;
    }
    &--outside  { color: rgba(145,151,161,0.4) !important; }
    &--disabled { color: rgba(145,151,161,0.3) !important; cursor: not-allowed; }

    &:not(.fm-cal__grid-cell--disabled):not(.fm-cal__grid-cell--selected):hover {
      background: $fm-line-soft;
    }
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    padding: 12px 12px 4px;
    span {
      font-size: 11px;
      font-weight: 600;
      /* WCAG 1.4.3: was #9197A1 (~2.94:1 on white). #6B7280 clears AA. */
      color: #6B7280;
      user-select: none;
    }
  }

  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 4px 12px 12px;
  }

  &__day {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 13px;
    border: none;
    background: none;
    cursor: pointer;
    transition: background 0.12s;
    margin: 0 auto;
    color: $fm-ink;

    &--selected {
      background: $fm-amber !important;
      color: $fm-ink;
      font-weight: 600;
    }
    &--today:not(.fm-cal__day--selected) {
      box-shadow: 0 0 0 1px $fm-amber;
      font-weight: 500;
    }
    &--outside  { color: rgba(145,151,161,0.3) !important; cursor: default; }
    &--disabled { color: rgba(145,151,161,0.3) !important; cursor: not-allowed; }

    &:not(.fm-cal__day--disabled):not(.fm-cal__day--outside):not(.fm-cal__day--selected):hover {
      background: $fm-line-soft;
    }
  }
}
</style>
